import { format } from "date-fns";
import moment from "moment";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  formatStatementApiEndDate,
  formatStatementApiStartDate,
  parseAmountInput,
  parseStatementApiFilterDate,
  startOfDay,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";

export const EMPTY_EXTRACT_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  types: [],
  categories: [],
  subAccountId: "",
  searchQuery: "",
};

export const resolveExtractFilters = (filters) => filters ?? EMPTY_EXTRACT_FILTERS;

export const EXTRACT_TYPE_OPTIONS = [
  { id: "credit", labelKey: "extract.typeCredit" },
  { id: "debit", labelKey: "extract.typeDebit" },
];

export const getExtractTypeOptions = () =>
  EXTRACT_TYPE_OPTIONS.map((option) => ({
    id: option.id,
    label: intl.get(option.labelKey),
  }));

export const getExtractTypeLabel = (typeId) => {
  const option = EXTRACT_TYPE_OPTIONS.find((item) => item.id === typeId);
  return option ? intl.get(option.labelKey) : typeId;
};

/** Normalize category id from API / filter selections. */
export const resolveExtractCategoryId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    const nested = value.id ?? value.Id ?? value.accountBalanceCategoryId;
    return resolveExtractCategoryId(nested);
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getExtractItemCategoryId = (item) =>
  resolveExtractCategoryId(item?.category?.id ?? item?.category?.Id ?? item?.category_id);

export const getExtractItemCategoryName = (item) =>
  String(item?.category?.name ?? item?.category?.Name ?? "").trim();

export const flattenTransactionDayGroups = (dayGroups = []) => {
  const flat = [];

  dayGroups.forEach((group) => {
    if (!group) return;
    const day = Object.keys(group)[0];
    (group[day] ?? []).forEach((item) => flat.push(item));
  });

  return flat;
};

export const regroupTransactionsByDay = (transactions = []) => {
  const groups = new Map();

  transactions.forEach((item) => {
    const day = moment(item.created_at).format("YYYY-MM-DD");
    if (!groups.has(day)) {
      groups.set(day, []);
    }
    groups.get(day).push(item);
  });

  return [...groups.entries()].map(([day, items]) => ({ [day]: items }));
};

export const paginateList = (items = [], page = 1, pageSize = 10) => {
  const safePage = Math.max(1, page);
  const safePageSize = Math.max(1, pageSize);
  const start = (safePage - 1) * safePageSize;
  return items.slice(start, start + safePageSize);
};

export const paginateExtractDayGroups = (dayGroups = [], page = 1, daysPerPage = 10) =>
  paginateList(dayGroups, page, daysPerPage);

export const filtersFromExtractFilterObj = (filterObj) => {
  const obj = filterObj ?? {};
  return {
    dateFrom: parseStatementApiFilterDate(obj.start_date),
    dateTo: parseStatementApiFilterDate(obj.end_date),
    amountMin: obj.start_amount
      ? String(Number(obj.start_amount) / 100).replace(".", ",")
      : "",
    amountMax: obj.end_amount
      ? String(Number(obj.end_amount) / 100).replace(".", ",")
      : "",
    types: [],
    categories: [],
    subAccountId: obj.sub_account_id || "",
    searchQuery: "",
  };
};

export const extractFilterObjFromFilters = (filters) => {
  const resolved = resolveExtractFilters(filters);
  return {
    start_date: formatStatementApiStartDate(resolved.dateFrom),
    end_date: formatStatementApiEndDate(resolved.dateTo),
    start_amount: (() => {
      const parsed = parseAmountInput(resolved.amountMin);
      return parsed === null ? "" : String(Math.round(parsed * 100));
    })(),
    end_amount: (() => {
      const parsed = parseAmountInput(resolved.amountMax);
      return parsed === null ? "" : String(Math.round(parsed * 100));
    })(),
    sub_account_id: resolved.subAccountId || "",
  };
};

export const hasActiveExtractFilters = (filters) => {
  const resolved = resolveExtractFilters(filters);
  return Boolean(resolved.dateFrom)
    || Boolean(resolved.dateTo)
    || resolved.amountMin !== ""
    || resolved.amountMax !== ""
    || resolved.types.length > 0
    || (resolved.categories?.length ?? 0) > 0
    || resolved.subAccountId !== ""
    || Boolean(resolved.searchQuery?.trim());
};

export const getExtractTransactionAmountCents = (item) =>
  Math.abs(Number(item?.amount) || Number(item?.operation_amount) || 0);

export const isExtractCreditTransaction = (item) => {
  const operation = String(item?.operation ?? "").toLowerCase();
  if (operation === "credit") return true;
  if (operation === "debit") return false;

  const amount = Number(item?.amount) || 0;
  const balanceBefore = Number(item?.balance_before);
  const balanceAfter = Number(item?.balance_after);
  const hasBalances =
    Number.isFinite(balanceBefore)
    && Number.isFinite(balanceAfter)
    && balanceBefore !== balanceAfter;

  // Some statement rows store credits with a non-positive amount; use balance movement.
  if (amount <= 0 && hasBalances) {
    return balanceAfter > balanceBefore;
  }

  return amount >= 0;
};

export const applyExtractClientFilters = (items, filters) => {
  const resolved = resolveExtractFilters(filters);
  const minAmount = parseAmountInput(resolved.amountMin);
  const maxAmount = parseAmountInput(resolved.amountMax);
  const dateFrom = resolved.dateFrom ? startOfDay(resolved.dateFrom) : null;
  const dateTo = resolved.dateTo ? endOfDay(resolved.dateTo) : null;

  return items.filter((item) => {
    const itemDate = moment(item.created_at).toDate();

    if (dateFrom && itemDate < dateFrom) return false;
    if (dateTo && itemDate > dateTo) return false;

    const amountCents = getExtractTransactionAmountCents(item);
    if (minAmount !== null && amountCents < Math.round(minAmount * 100)) return false;
    if (maxAmount !== null && amountCents > Math.round(maxAmount * 100)) return false;

    if (resolved.types.length > 0) {
      const typeId = isExtractCreditTransaction(item) ? "credit" : "debit";
      if (!resolved.types.includes(typeId)) return false;
    }

    if (resolved.categories?.length > 0) {
      const selectedIds = resolved.categories
        .map((value) => resolveExtractCategoryId(value))
        .filter((id) => id != null);
      const itemCategoryId = getExtractItemCategoryId(item);
      if (itemCategoryId == null || !selectedIds.includes(itemCategoryId)) return false;
    }

    if (resolved.searchQuery?.trim()) {
      const query = resolved.searchQuery.trim().toLowerCase();
      const haystack = [
        item.description,
        item.name,
        item.operation_id,
        item.category?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!haystack.includes(query)) return false;
    }

    return true;
  });
};

export const isExtractChipFilterActive = (filters, chipId) => {
  const resolved = resolveExtractFilters(filters);
  switch (chipId) {
    case "date":
      return Boolean(resolved.dateFrom || resolved.dateTo);
    case "amount":
      return resolved.amountMin !== "" || resolved.amountMax !== "";
    case "type":
      return resolved.types.length > 0;
    case "category":
      return (resolved.categories?.length ?? 0) > 0;
    case "subAccount":
      return resolved.subAccountId !== "";
    default:
      return false;
  }
};

export const buildExtractFilterTags = (filters, subAccounts = [], categoryOptions = []) => {
  const resolved = resolveExtractFilters(filters);
  const tags = [];
  const categoryLabelById = new Map(
    (categoryOptions || []).map((option) => [
      resolveExtractCategoryId(option.id ?? option.Id),
      option.label || option.name || option.Name || String(option.id),
    ]),
  );

  if (resolved.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: resolved.searchQuery.trim(),
    });
  }

  if (resolved.dateFrom || resolved.dateTo) {
    const fromLabel = resolved.dateFrom ? formatFilterDate(resolved.dateFrom) : "…";
    const toLabel = resolved.dateTo ? formatFilterDate(resolved.dateTo) : "…";
    tags.push({
      key: "date",
      iconType: "date",
      value: `${fromLabel} – ${toLabel}`,
    });
  }

  if (resolved.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(resolved.amountMin),
    });
  }

  if (resolved.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(resolved.amountMax),
    });
  }

  resolved.types.forEach((typeId) => {
    tags.push({
      key: `type-${typeId}`,
      iconType: "type",
      value: getExtractTypeLabel(typeId),
      removeKey: "types",
      removeValue: typeId,
    });
  });

  (resolved.categories || []).forEach((categoryValue) => {
    const categoryId = resolveExtractCategoryId(categoryValue);
    if (categoryId == null) return;
    tags.push({
      key: `category-${categoryId}`,
      iconType: "category",
      value: categoryLabelById.get(categoryId) || String(categoryId),
      removeKey: "categories",
      removeValue: categoryId,
    });
  });

  if (resolved.subAccountId) {
    const subAccount = subAccounts.find((sa) => sa.subAccountId === resolved.subAccountId);
    tags.push({
      key: "subAccountId",
      iconType: "subAccount",
      value: subAccount?.name || resolved.subAccountId,
    });
  }

  return tags;
};

export const removeExtractFilterTag = (filters, tagKey, tagMeta = {}) => {
  const resolved = resolveExtractFilters(filters);
  if (tagKey.startsWith("type-")) {
    const typeId = tagKey.replace("type-", "");
    return {
      ...resolved,
      types: resolved.types.filter((item) => item !== typeId),
    };
  }

  if (tagKey.startsWith("category-")) {
    const categoryId = resolveExtractCategoryId(tagKey.replace("category-", ""));
    return {
      ...resolved,
      categories: (resolved.categories || []).filter(
        (item) => resolveExtractCategoryId(item) !== categoryId,
      ),
    };
  }

  switch (tagKey) {
    case "date":
    case "dateFrom":
    case "dateTo":
      return { ...resolved, dateFrom: null, dateTo: null };
    case "amountMin":
      return { ...resolved, amountMin: "" };
    case "amountMax":
      return { ...resolved, amountMax: "" };
    case "types":
      return { ...resolved, types: [] };
    case "categories":
      return tagMeta.removeValue != null
        ? {
            ...resolved,
            categories: (resolved.categories || []).filter(
              (item) => resolveExtractCategoryId(item) !== resolveExtractCategoryId(tagMeta.removeValue),
            ),
          }
        : { ...resolved, categories: [] };
    case "subAccountId":
      return { ...resolved, subAccountId: "" };
    case "searchQuery":
      return { ...resolved, searchQuery: "" };
    default:
      return tagMeta.removeKey === "types" && tagMeta.removeValue
        ? {
            ...resolved,
            types: resolved.types.filter((item) => item !== tagMeta.removeValue),
          }
        : tagMeta.removeKey === "categories" && tagMeta.removeValue != null
          ? {
              ...resolved,
              categories: (resolved.categories || []).filter(
                (item) =>
                  resolveExtractCategoryId(item) !== resolveExtractCategoryId(tagMeta.removeValue),
              ),
            }
          : resolved;
  }
};

const FEE_KEYWORDS = /tarifa|taxa|fee|comiss/i;

export const isExtractFeeTransaction = (item) => {
  const categoryName = item?.category?.name ?? "";
  const description = item?.description ?? "";
  const name = item?.name ?? "";
  return FEE_KEYWORDS.test(categoryName) || FEE_KEYWORDS.test(description) || FEE_KEYWORDS.test(name);
};

export const computeExtractPeriodSummary = (items = []) => {
  let totalCredits = 0;
  let totalDebits = 0;

  items.forEach((item) => {
    const amount = getExtractTransactionAmountCents(item);
    if (amount === 0) return;

    if (isExtractCreditTransaction(item)) {
      totalCredits += amount;
    } else {
      totalDebits += amount;
    }
  });

  return {
    totalCredits,
    totalDebits,
  };
};

export const estimateExtractTotal = (page, pageCount, rowsPerPage, currentPageCount) => {
  if (!pageCount || pageCount <= 0) return currentPageCount;
  if (page < pageCount) return pageCount * rowsPerPage;
  return (page - 1) * rowsPerPage + currentPageCount;
};

export const hasExtractClientSideFilters = (filters) => {
  const resolved = resolveExtractFilters(filters);
  return (
    resolved.types.length > 0
    || (resolved.categories?.length ?? 0) > 0
    || Boolean(resolved.searchQuery?.trim())
  );
};

export const hasExtractApiFilters = (filterObj) => {
  const obj = filterObj ?? {};
  return Boolean(
    obj.start_date
    || obj.end_date
    || obj.start_amount
    || obj.end_amount
    || obj.sub_account_id,
  );
};

export const resolveExtractApiTotalRecords = (data = {}) => {
  const candidates = [
    data.rowCount,
    data.row_count,
    data.totalRecords,
    data.total_records,
    data.totalItems,
    data.total_items,
    data.totalCount,
    data.total_count,
    data.recordCount,
    data.record_count,
    data.total,
    data.count,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return null;
};

export const computeExtractPaginationTotal = ({
  page,
  pageCount,
  rowsPerPage,
  filteredPageCount,
  rawPageCount,
  hasClientSideFilters,
  hasApiFilters,
  apiTotalRecords,
}) => {
  const displayed = Math.max(0, filteredPageCount ?? 0);
  const raw = Math.max(0, rawPageCount ?? displayed);
  const filtersReducedPage = displayed < raw;

  if (
    apiTotalRecords != null
    && !hasClientSideFilters
    && !filtersReducedPage
  ) {
    return apiTotalRecords;
  }

  if (hasClientSideFilters || filtersReducedPage) {
    return (page - 1) * rowsPerPage + displayed;
  }

  if (hasApiFilters) {
    return (page - 1) * rowsPerPage + displayed;
  }

  return estimateExtractTotal(page, pageCount, rowsPerPage, displayed);
};

export const exportExtractCsv = (items) => {
  const headers = ["Data", "Descrição", "Valor", "Tipo", "Saldo após"];

  const rows = items.map((item) => {
    const amount = getExtractTransactionAmountCents(item);
    const isCredit = isExtractCreditTransaction(item);
    return [
      moment(item.created_at).format("DD/MM/YYYY HH:mm"),
      item.description || "",
      amount.toFixed(2).replace(".", ","),
      isCredit ? intl.get("extract.typeCredit") : intl.get("extract.typeDebit"),
      String(Math.abs(item.balance_after ?? 0)).replace(".", ","),
    ];
  });

  downloadCsv(headers, rows, `extrato-${format(new Date(), "yyyy-MM-dd")}.csv`);
};
